import { z } from 'zod';

const optionalText = z.string().trim().max(255).optional().or(z.literal(''));

export const providerSchema = z.object({
  firstName: z.string().trim().min(1, 'Required').max(255),
  lastName: z.string().trim().min(1, 'Required').max(255),
  email: z.string().trim().email('Invalid email'),
  phoneNumber: optionalText,
  department: optionalText,
  position: optionalText,
  empeonId: optionalText,
  payCode: z.enum(['Reg', '1099R']).optional(),
});
export type ProviderFormData = z.infer<typeof providerSchema>;

export const STUDENT_GRADES = [
  'Nursery',
  'Kindergarten',
  'Pre1-A',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
] as const;

export const studentSchema = z.object({
  firstName: z.string().trim().min(1, 'Required').max(255),
  lastName: z.string().trim().min(1, 'Required').max(255),
  grade: z.enum(STUDENT_GRADES, { errorMap: () => ({ message: 'Grade is required' }) }),
  birthDate: optionalText,
  nycId: optionalText,
  gender: optionalText,
});
export type StudentFormData = z.infer<typeof studentSchema>;

export const signerSchema = z.object({
  firstName: z.string().trim().min(1, 'Required'),
  lastName: z.string().trim().min(1, 'Required'),
  email: z.string().trim().email('Invalid email'),
  isMain: z.boolean(),
});
export type SignerFormData = z.infer<typeof signerSchema>;

export const placementSchema = z
  .object({
    providerId: z.string().uuid('Pick a provider'),
    studentId: z.string().uuid('Pick a student'),
    schoolId: z.string().uuid('Pick a school'),
    schoolYearLabel: z.string().regex(/^\d{2}-\d{2}$/, 'Format 26-27'),
    startDate: z.string().min(1, 'Required'),
    finishDate: z.string().min(1, 'Required'),
    hours: z.coerce.number().positive('Hours per week required'),
    hoursPerDay: z.coerce.number().positive().optional().or(z.literal('')),
    payRate: z.coerce.number().positive('Pay rate required'),
    group2Rate: z.coerce.number().nonnegative().optional().or(z.literal('')),
    group3Rate: z.coerce.number().nonnegative().optional().or(z.literal('')),
    paymentMethod: z.string().min(1, 'Required'),
    doeInvoiceType: z.string().min(1, 'Required'),
    doeRate: z.coerce.number().nonnegative().optional().or(z.literal('')),
  })
  .refine((d) => new Date(d.finishDate) > new Date(d.startDate), {
    message: 'Finish date must be after start date',
    path: ['finishDate'],
  });
export type PlacementFormData = z.infer<typeof placementSchema>;

export const numberOrNull = (v: number | '' | undefined): number | null =>
  typeof v === 'number' ? v : null;

/** Default school-year label for today, e.g. "26-27" (school year starts in July). */
export const currentSchoolYearLabel = (now = new Date()): string => {
  const start = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const pad = (y: number) => String(y % 100).padStart(2, '0');
  return `${pad(start)}-${pad(start + 1)}`;
};
